import { useAppContext } from "@context/app";
import DialCodeModal from "./DialCodeModal";
import { positiveNumberPattern } from "@utils/constants";
import { useIsMount } from "@utils/helpers";
import cn from "classnames";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { DialCodeItem } from "@types";


interface PhoneFieldProps {
  defaultValue?: string;
  onChange: (value: string) => void;
  errors: string[];
  label: string;
}

const PhoneField: React.FC<PhoneFieldProps> = ({
  defaultValue,
  onChange,
  errors,
  label,
}) => {
  const { country, syncData } = useAppContext();
  const t = useTranslations();
  const { locale } = useRouter();

  const [value, setValue] = useState<string>("");
  const [openCountries, setOpenCountries] = useState(false);
  const [selectedDialCode, setSelectedDialCode] = useState<DialCodeItem | undefined>();

  const isMount = useIsMount();

  // Find default dial code based on country
  useEffect(() => {
    if (syncData?.dialcodes && country && !selectedDialCode) {
      const defaultDialCode = syncData.dialcodes.find((dc) =>
        dc.code2.toLowerCase() === country.code2.toLowerCase()
      );
      setSelectedDialCode(defaultDialCode || syncData.dialcodes[0]);
    }
  }, [syncData, country, selectedDialCode]);

  useEffect(() => {
    if (!defaultValue) {
      setValue("");
      return;
    }

    if (syncData?.dialcodes) {
      const setDefaultValue = async () => {
        const parsePhoneNumber = (await import("libphonenumber-js")).default;
        const phoneNumber = parsePhoneNumber(defaultValue);
        if (phoneNumber) {
          setValue(phoneNumber.nationalNumber);
          setSelectedDialCode((prev) =>
            syncData.dialcodes.find((x) =>
              x.dialcode.includes(phoneNumber.countryCallingCode)
            ) || prev
          );
        }
      }
      setDefaultValue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, syncData]);

  const handelChangeValue = (text: string) => {
    setValue(text);
    onChange(text ? `${selectedDialCode?.dialcode}${text}` : `${text}`);
  };

  const handelSelectedDialCode = (dialCode: DialCodeItem) => {
    setSelectedDialCode(dialCode);
    setOpenCountries(false);
  };

  useEffect(() => {
    if (!isMount) {
      handelChangeValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDialCode]);

  return (
    <>
      <DialCodeModal
        open={openCountries}
        onClose={() => setOpenCountries(false)}
        dialcodes={syncData?.dialcodes || []}
        onSelect={handelSelectedDialCode}
      />

      <div className="md:w-full sm:w-full xs:w-full w-full p-1">
        <div className="form__group w-full h-14 bg-[#F5F5F5] rounded-xl relative">
          <input
            value={value}
            onChange={(e) => {
              const v = e.target.value;
              if (
                new RegExp(positiveNumberPattern, "i").test(v as string) ||
                !v
              ) {
                handelChangeValue(v);
              }
            }}
            type="text"
            className={cn(
              "form__control border-2 border-[transparent] outline-0 bg-transparent h-full w-full rounded-xl pt-[10px] pb-2 px-4 pl-20 text-sm font-normal",
              errors.length ? "err-input" : ""
            )}
            autoComplete="nope"
          />
          <label onClick={() => setOpenCountries(true)} className=" cursor-pointer absolute top-[18px] left-0 pl-2 text-[#333] text-sm font-normal flex items-center">
            <div
              className=" w-6 h-6 rounded-full overflow-hidden border mr-1 relative"
            >
              <Image
                src={selectedDialCode?.image.url || ""}
                alt={selectedDialCode?.name || ""}
                fill={true}
              />
            </div>{" "}
            {selectedDialCode?.dialcode}
          </label>
          {locale?.includes("ar") ? (
            <label
              className={cn(
                "form__label px-4 text-[#999999] text-sm font-normal absolute ",
                errors.length ? "err-label" : "",
                value ? "top-label" : "",
                locale?.includes("ar") ? "right-0" : "left-0"
              )}
            >
              {label}
            </label>
          ) : (
            <label
              className={cn(
                "form__label-top px-9 text-[#999999] text-sm font-normal",
                errors.length ? "err-label" : "",
                "top-label",
                "left-0"
              )}
            >
              {label}
            </label>
          )}
        </div>
        {errors.length
          ? errors.map((err) => (
            <div key={`err-phone-${err}`} className="w-full mt-1 pl-2">
              <p className={`text-red-500 text-xs font-normal ${locale?.includes("ar-") ? " float-right" : " float-left"}`}>{err}</p>
            </div>
          ))
          : null}
      </div>
    </>
  );
};

export default PhoneField;
